import { normalizeDevice } from 'features/integrations/utils/integrationNormalize';
import Cookies from 'js-cookie';
import apiClient from 'services/_infra/apiClient';
import { loginfo } from 'services/_infra/util/apiUsageLogger';
import { devicesApi } from './devices.api';

// Allows the updating of one or more devices in the RTK Query cache based on an event received from the server
export const applyDeviceUpdatesFromEvent = (dispatch, updates) => {
  let updatesArray;

  if (Array.isArray(updates)) {
    updatesArray = updates;
  } else if (updates) {
    updatesArray = [updates];
  } else {
    updatesArray = [];
  }

  if (!updatesArray.length) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => {
        if (!draft.length) return;

        updatesArray.forEach((update) => {
          const indexOfDeviceToUpdate = draft.findIndex((device) => device.id === update.id);

          if (indexOfDeviceToUpdate < 0) return; // If the device is not found in the cache, skip it

          const normalizedUpdate = normalizeDevice(update);

          draft[indexOfDeviceToUpdate] = {
            ...draft[indexOfDeviceToUpdate],
            ...normalizedUpdate,
            data: [
              ...(normalizedUpdate?.data || []),
              ...(draft[indexOfDeviceToUpdate]?.data || []),
            ],
          };
        });
      },
    ),
  );
};

// Replaces the entire cached device types array with a new one based on event received from the server
export const updateDeviceTypesFromEvent = (dispatch, deviceTypes) => {
  if (!deviceTypes) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getAllAvailableDeviceTypes.name,
      undefined,
      () => deviceTypes,
    ),
  );
};

export const addDeviceFromEvent = (dispatch, eventData) => {
  const {device, deviceTypes} = eventData || {};

  if (!device) return;

  const normalizedDevice = normalizeDevice(device);
  if (!normalizedDevice?.id) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => {
        const existingDeviceIndex = draft.findIndex((device) => device.id === normalizedDevice.id); // Check if the device already exists in the cache

        if (existingDeviceIndex >= 0) {
          draft[existingDeviceIndex] = {
            ...draft[existingDeviceIndex],
            ...normalizedDevice,
          };
        } else {
          draft.push(normalizedDevice);
        }

        if (deviceTypes) {
          updateDeviceTypesFromEvent(dispatch, deviceTypes);
        }
      },
    ),
  );
};

export const removeDeviceFromEvent = (dispatch, eventData) => {
  const {id, deviceTypes} = eventData || {};
  if (!id) return;

  dispatch(
    devicesApi.util.updateQueryData(
      devicesApi.endpoints.getConnectedDevices.name,
      undefined,
      (draft) => draft.filter((device) => device.id !== id),
    ),
  );

  if (deviceTypes) {
    updateDeviceTypesFromEvent(dispatch, deviceTypes);
  }
};

export async function getConnectedDevices() {
  try {
    return await apiClient.get('/getConnectedDevices_v2');
  } catch (error) {
    console.error('getConnectedDevices_v2 failed.', error);
  }
}

export function getConnectedDevicesEventSource_v2() {
  try {
    const url = `${
      process.env.PUBLIC_API_URL
    }/getConnectedDevices_Event_v2Redux?token=${encodeURIComponent(Cookies.get('accessToken'))}`;
    return new EventSource(url);
  } catch (error) {
    console.error('getConnectedDevices_Event_v2Redux failed.', error);
  }
}

// This function should not be cached with RTK Query because it is likely to be called with different parameters each time
export async function getNodeDataByTimestamps(startTime, endTime, ids) {
  try {
    return await apiClient.get('/getNodeDataByTimestamps_v2', {
      params: {
        startTime,
        endTime,
        ids,
      },
    });
  } catch (error) {
    console.error('getNodeDataByTimestamps_v2 failed.', error);
  }
}

export async function getDeviceTypeMap() {
  try {
    return await apiClient.get('/getDeviceTypeMap_v2');
  } catch (error) {
    console.error('getDeviceTypeMap_v2 failed.', error);
  }
}
export async function getSystemTypeMap() {
  try {
    return await apiClient.get('/getSystemTypeMap_v2');
  } catch (error) {
    console.error('getSystemTypeMap_v2 failed.', error);
  }
}

export async function getAudioFile(audioId) {
  try {
    loginfo();
    return await apiClient.get(`/getSirenAudio_v2/${audioId}`, {
      responseType: 'blob',
    });
  } catch (error) {
    console.error('getAudioFile logging failed.', error);
  }
}

export async function GetHLSStream(device) {
  if (device.device_type?.toLowerCase() !== 'overwatch vms') return null;

  try {
    return await apiClient.get(`/camera_v2/${device.id}/hls/index.m3u8`);
  } catch (error) {
    console.error('camera_v2 failed.', error);
  }
}
